import { useState } from 'react';
import clsx from 'clsx';
import { Button } from '../../common/Catalyst/button';
import { Text } from '../../common/Catalyst/text';
import { CommentVisibility } from '../../clients/rust-api-client';
import { useThreadCopy } from './commentLabels';
import AttachmentPicker from './AttachmentPicker';
import { useAttachmentUploads } from './useAttachmentUploads';

interface Props {
  /**
   * Which thread this composer posts to.
   *
   * **Required, and never optional.** `CommentCreate.visibility` defaults
   * to `internal` on the server, so a composer that could omit it would
   * silently swallow a reply the homeowner was meant to receive. Making it
   * mandatory here is what stops that payload being constructible.
   */
  visibility: CommentVisibility;
  mode?: 'create' | 'edit';
  initialContent?: string;
  /** Set on a reply, so the thread nests and the label can say who. */
  replyingTo?: string;
  busy?: boolean;
  autoFocus?: boolean;
  /** Offer a file picker. Off for an edit, which creates no new target. */
  allowAttachments?: boolean;
  /**
   * Post the comment and hand back its id.
   *
   * The id is what makes the attachment phase retryable on its own. A
   * composer that could only re-run the whole submit would post the
   * comment a second time, and a duplicate comment is far worse than a
   * missing photo.
   */
  onSubmit: (content: string) => Promise<string | void>;
  onCancel?: () => void;
  /** Called once the comment and all of its files have landed. */
  onSettled?: () => void;
}

export function CommentComposer({
  visibility,
  mode = 'create',
  initialContent = '',
  replyingTo,
  busy = false,
  autoFocus = false,
  allowAttachments = false,
  onSubmit,
  onCancel,
  onSettled,
}: Props) {
  const [content, setContent] = useState(initialContent);
  const [touched, setTouched] = useState(false);
  /**
   * Set once phase one has succeeded.
   *
   * Its presence means "the comment exists; only files are outstanding",
   * which is what routes Retry to the attachment endpoint alone.
   */
  const [postedCommentId, setPostedCommentId] = useState<string | null>(null);
  const uploads = useAttachmentUploads();

  const copy = useThreadCopy(visibility);
  const trimmed = content.trim();
  const isEmpty = trimmed.length === 0;

  const finish = () => {
    setContent('');
    setTouched(false);
    setPostedCommentId(null);
    uploads.reset();
    onSettled?.();
  };

  const handleSubmit = async () => {
    setTouched(true);
    if (busy) return;

    // Phase two only. The comment already exists, so re-running phase one
    // would post it a second time.
    if (postedCommentId) {
      const clean = await uploads.uploadAll({
        kind: 'comment',
        id: postedCommentId,
      });
      if (clean) finish();
      return;
    }

    if (isEmpty) return;

    let commentId: string | void;
    try {
      commentId = await onSubmit(trimmed);
    } catch {
      // Phase one failed. The caller has surfaced the reason; keep the
      // text and the staged files so the author can try again rather than
      // retyping what they just wrote.
      return;
    }

    if (typeof commentId !== 'string' || uploads.staged.length === 0) {
      // Nothing to attach, or an edit. The parent decides whether the
      // composer survives: a reply or an edit unmounts, the root composer
      // stays and is cleared.
      finish();
      return;
    }

    setPostedCommentId(commentId);
    const clean = await uploads.uploadAll({ kind: 'comment', id: commentId });
    // A failed attachment never rolls the comment back. A shared comment
    // has already been delivered and emailed; deleting it to tidy up would
    // retract something the homeowner was told about. The text stays and
    // the files are retried on their own.
    if (clean) finish();
  };

  const submitLabel =
    mode === 'edit'
      ? 'Save changes'
      : replyingTo
        ? `Reply ${copy.label === 'Internal' ? 'internally' : 'to the thread'}`
        : copy.submitLabel;

  // ⌘/Ctrl+Enter posts, as in Linear; plain Enter stays a newline.
  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const placeholder =
    mode === 'edit'
      ? undefined
      : replyingTo
        ? 'Leave a reply…'
        : copy.placeholder;

  // Linear's composer: a quiet card, the text first, a toolbar under it.
  // The destination is still named three times — the hint, the tint and
  // the submit button (see commentLabels.ts) — just more quietly.
  return (
    <div className={clsx('rounded-lg border', copy.composerTint)}>
      {/* Said before the textarea, not after it: the destination has to be
          read before the message is written, not after. */}
      <p className="px-3 pt-2.5 text-[12px] text-fg-muted">
        {mode === 'edit' ? 'Editing your comment.' : copy.composerHint}
      </p>

      <textarea
        name="content"
        rows={mode === 'create' && !replyingTo ? 3 : 2}
        value={content}
        autoFocus={autoFocus}
        disabled={busy || postedCommentId !== null}
        aria-invalid={touched && isEmpty ? true : undefined}
        onChange={(e) => setContent(e.target.value)}
        onBlur={() => setTouched(true)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        className="block w-full resize-y border-0 bg-transparent px-3 py-2 text-[14px] leading-6 text-fg placeholder:text-fg-muted focus:outline-none focus:ring-0 disabled:opacity-60"
      />

      {touched && isEmpty && !postedCommentId && (
        <p className="px-3 text-[12px] text-danger">A comment cannot be empty.</p>
      )}

      {postedCommentId && (
        <Text className="px-3">
          Your comment was posted. These files still need to be attached.
        </Text>
      )}

      <div className="flex flex-wrap items-center gap-2 px-2 pb-2 pt-1">
        {allowAttachments ? (
          <div className="min-w-0 flex-1">
            <AttachmentPicker
              compact
              staged={uploads.staged}
              disabled={busy}
              onStage={uploads.stage}
              onRemove={uploads.remove}
            />
          </div>
        ) : (
          <div className="flex-1" />
        )}
        {postedCommentId ? (
          <Button plain onClick={finish} disabled={busy}>
            Done
          </Button>
        ) : (
          onCancel && (
            <Button plain onClick={onCancel} disabled={busy}>
              Cancel
            </Button>
          )
        )}
        <Button
          color="sky"
          disabled={busy || (postedCommentId ? !uploads.hasUploadable : isEmpty)}
          onClick={handleSubmit}
          title="⌘/Ctrl + Enter"
        >
          {busy
            ? 'Posting…'
            : postedCommentId
              ? 'Retry attachments'
              : submitLabel}
        </Button>
      </div>
    </div>
  );
}

export default CommentComposer;
